import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageShell } from './layout';
import { getCollaborationPartners, resolvePartnerImageUrl } from '../api/partners';
import styles from '../styles/Collaborazioni.module.css';
import FaIcon from './FaIcon';

// Collaboration benefits
const COLLAB_BENEFITS = [
  {
    icon: '🎯',
    title: 'Contenuti complementari',
    description: 'I nostri giochi si integrano perfettamente con lezioni, podcast e risorse educative, offrendo pratica attiva ai tuoi studenti.'
  },
  {
    icon: '📈',
    title: 'Crescita reciproca',
    description: 'Raggiungiamo insieme nuovi studenti di italiano in tutto il mondo, aumentando la visibilità dei nostri progetti.'
  },
  {
    icon: '🎨',
    title: 'Personalizzazione',
    description: 'Creiamo landing page personalizzate e messaggi di benvenuto su misura per la tua community.'
  },
  {
    icon: '🤝',
    title: 'Partnership flessibili',
    description: 'Che tu sia un creator, una scuola, una casa editrice o un\'app, troviamo insieme il modo migliore per collaborare.'
  },
  {
    icon: '🎁',
    title: 'Vantaggi esclusivi',
    description: 'Offri alla tua community sconti speciali e accesso anticipato a nuovi giochi e funzionalità.'
  },
  {
    icon: '💬',
    title: 'Assistenza su misura',
    description: 'Un contatto diretto per rispondere alle tue esigenze e supportare al meglio la tua community.'
  }
];

// Who we're looking for
const IDEAL_PARTNERS = [
  { emoji: '🎙️', label: 'Podcaster di italiano' },
  { emoji: '📹', label: 'YouTuber e creator' },
  { emoji: '🏫', label: 'Insegnanti e scuole' },
  { emoji: '📱', label: 'App educative' },
  { emoji: '✍️', label: 'Blogger linguistici' },
  { emoji: '🌍', label: 'Community di studenti' },
  { emoji: '📚', label: 'Case editrici' },
];
const PARTNER_SKELETON_COUNT = 3;

const Collaborazioni = () => {
  const [partners, setPartners] = useState([]);
  const [isLoadingPartners, setIsLoadingPartners] = useState(true);
  const [partnersError, setPartnersError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadPartners = async () => {
      setIsLoadingPartners(true);
      setPartnersError(null);

      const result = await getCollaborationPartners();
      if (cancelled) return;

      if (result?.success) {
        setPartners(Array.isArray(result.partners) ? result.partners : []);
      } else {
        setPartners([]);
        setPartnersError('Impossibile caricare i partner in questo momento.');
      }
      setIsLoadingPartners(false);
    };

    loadPartners();

    return () => {
      cancelled = true;
    };
  }, []);

  const partnersForCollaborations = useMemo(
    () => [...partners].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)),
    [partners]
  );
  const ctaDelayBase = isLoadingPartners ? PARTNER_SKELETON_COUNT : partnersForCollaborations.length;

  return (
    <PageShell>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        {/* Animated background elements */}
        <div className={styles.bgElements}>
          {['🤝', '🎮', '📚', '🌍', '✨', '🎯', '💡', '🚀'].map((emoji, i) => (
            <span key={i} className={styles.bgEmoji}>{emoji}</span>
          ))}
        </div>

        {/* Connection lines pattern */}
        <svg className={styles.connectionLines} viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d="M10,90 Q30,50 50,50 T90,10" fill="none" stroke="currentColor" strokeWidth="0.3" opacity="0.15" />
          <path d="M0,70 Q40,40 60,50 T100,20" fill="none" stroke="currentColor" strokeWidth="0.2" opacity="0.1" />
          <path d="M20,100 Q50,60 70,60 T100,40" fill="none" stroke="currentColor" strokeWidth="0.25" opacity="0.12" />
        </svg>

        <div className={styles.heroContent}>
          <div className={styles.heroEmoji}>🤝</div>
          <h1 className={styles.heroTitle}>Collaboriamo</h1>
          <p className={styles.heroSubtitle}>
            Portiamo il divertimento dell'italiano a studenti in tutto il mondo.
            <br />
            Uniamo le forze per rendere l'apprendimento un gioco.
          </p>
        </div>

        <div className={styles.scrollHint}>
          <span>Scopri come</span>
          <FaIcon className="fas fa-chevron-down"  />
        </div>
      </section>

      {/* Mission Section */}
      <section className={styles.missionSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.missionContent}>
            <div className={styles.missionIcon}>🎯</div>
            <h2 className={styles.missionTitle}>La nostra missione</h2>
            <p className={styles.missionText}>
              <strong>Giochini di Parole</strong> vuole rendere lo studio dell'italiano 
              un <em>piacere quotidiano</em>. Crediamo che imparare giocando sia il modo 
              più efficace per costruire abitudini durature.
            </p>
            <p className={styles.missionText}>
              Per questo cerchiamo partner che condividano la nostra visione: 
              creator, insegnanti, scuole e community che vogliono offrire ai propri 
              studenti un'esperienza di apprendimento unica e coinvolgente.
            </p>
          </div>
        </div>
      </section>

      {/* Current Partners */}
      <section className={styles.partnersSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEmoji}>🌟</span>
            <h2 className={styles.sectionTitle}>I nostri partner</h2>
            <p className={styles.sectionSubtitle}>
              Collaboriamo già con creator e community fantastiche
            </p>
          </div>

          <div className={styles.partnersGrid}>
            {isLoadingPartners ? (
              Array.from({ length: PARTNER_SKELETON_COUNT }).map((_, index) => (
                <article
                  key={`partner-skeleton-${index}`}
                  className={`${styles.partnerCard} ${styles.skeletonCard}`}
                  style={{ animationDelay: `${index * 0.08}s` }}
                  aria-hidden="true"
                >
                  <div className={styles.partnerImageWrapper}>
                    <div className={styles.skeletonImage} />
                  </div>
                  <div className={styles.partnerInfo}>
                    <div className={`${styles.skeletonLine} ${styles.skeletonTitle}`} />
                    <div className={`${styles.skeletonLine} ${styles.skeletonText}`} />
                    <div className={`${styles.skeletonLine} ${styles.skeletonTextShort}`} />
                    <div className={styles.skeletonTags}>
                      <span className={`${styles.skeletonLine} ${styles.skeletonTag}`} />
                      <span className={`${styles.skeletonLine} ${styles.skeletonTag}`} />
                      <span className={`${styles.skeletonLine} ${styles.skeletonTag}`} />
                    </div>
                    <div className={`${styles.skeletonLine} ${styles.skeletonLink}`} />
                  </div>
                </article>
              ))
            ) : partnersError ? (
              <article className={`${styles.partnerCard} ${styles.stateCard}`}>
                <div className={styles.partnerInfo}>
                  <h3 className={styles.stateTitle}>Partner non disponibili</h3>
                  <p className={styles.stateText}>{partnersError}</p>
                </div>
              </article>
            ) : partnersForCollaborations.length === 0 ? (
              <article className={`${styles.partnerCard} ${styles.stateCard}`}>
                <div className={styles.partnerInfo}>
                  <h3 className={styles.stateTitle}>Nessun partner pubblicato</h3>
                  <p className={styles.stateText}>
                    Torna presto per vedere le prossime collaborazioni.
                  </p>
                </div>
              </article>
            ) : (
              partnersForCollaborations.map((partner, index) => (
                <article 
                  key={partner.id || partner.slug} 
                  className={styles.partnerCard}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className={styles.partnerImageWrapper}>
                    {partner.image_url ? (
                      <img 
                        src={resolvePartnerImageUrl(partner.image_url)}
                        alt={partner.name}
                        className={styles.partnerImage}
                        loading="lazy"
                      />
                    ) : (
                      <div className={styles.ctaImagePlaceholder}>
                        <span className={styles.ctaEmoji}>🤝</span>
                      </div>
                    )}
                  </div>
                  <div className={styles.partnerInfo}>
                    <h3 className={styles.partnerName}>{partner.name}</h3>
                    <p className={styles.partnerDescription}>{partner.description}</p>
                    <div className={styles.partnerTags}>
                      {(partner.tags || []).map((tag, i) => (
                        <span key={i} className={styles.tag}>{tag}</span>
                      ))}
                    </div>
                    {partner.link && (
                      <a 
                        href={partner.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={styles.partnerLink}
                      >
                        <span>Visita</span>
                        <FaIcon className="fas fa-external-link-alt"  />
                      </a>
                    )}
                  </div>
                </article>
              ))
            )}

            {/* CTA Partner Card */}
            <Link 
              to="/contatti" 
              className={`${styles.partnerCard} ${styles.ctaPartnerCard}`}
              style={{ animationDelay: `${ctaDelayBase * 0.1}s` }}
            >
              <div className={styles.partnerImageWrapper}>
                <div className={styles.ctaImagePlaceholder}>
                  <span className={styles.ctaEmoji}>🚀</span>
                </div>
              </div>
              <div className={styles.partnerInfo}>
                <h3 className={styles.partnerName}>Il tuo progetto</h3>
                <p className={styles.partnerDescription}>
                  Insegni italiano o hai una community di studenti? Collaboriamo insieme!
                </p>
                <div className={styles.partnerTags}>
                  <span className={styles.tag}>Creator</span>
                  <span className={styles.tag}>Scuole</span>
                  <span className={styles.tag}>App</span>
                  <span className={styles.tag}>Case editrici</span>
                </div>
                <span className={styles.ctaLink}>
                  <span>Contattaci</span>
                  <FaIcon className="fas fa-arrow-right"  />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className={styles.benefitsSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEmoji}>✨</span>
            <h2 className={styles.sectionTitle}>Perché collaborare con noi</h2>
            <p className={styles.sectionSubtitle}>
              Una partnership che crea valore
            </p>
          </div>

          <div className={styles.benefitsGrid}>
            {COLLAB_BENEFITS.map((benefit, index) => (
              <div key={index} className={styles.benefitCard}>
                <div className={styles.benefitIcon}>{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We're Looking For */}
      <section className={styles.lookingForSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEmoji}>🔍</span>
            <h2 className={styles.sectionTitle}>Chi cerchiamo</h2>
            <p className={styles.sectionSubtitle}>
              Se ti riconosci in una di queste categorie, parliamone!
            </p>
          </div>

          <div className={styles.tagsCloud}>
            {IDEAL_PARTNERS.map((partner, index) => (
              <span key={index} className={styles.partnerTag}>
                <span className={styles.tagEmoji}>{partner.emoji}</span>
                <span className={styles.tagLabel}>{partner.label}</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          <span className={styles.ctaEmoji}>💌</span>
          <h2 className={styles.ctaTitle}>Iniziamo a collaborare!</h2>
          <p className={styles.ctaText}>
            Hai un'idea per una partnership? Sei un creator o gestisci una community 
            di studenti di italiano? Ci piacerebbe conoscerti!
          </p>
          <div className={styles.ctaButtons}>
            <Link to="/contatti" className={styles.ctaButtonPrimary}>
              <FaIcon className="fas fa-paper-plane"  />
              <span>Scrivici</span>
            </Link>
            <Link to="/chi-siamo" className={styles.ctaButtonSecondary}>
              <FaIcon className="fas fa-users"  />
              <span>Chi siamo</span>
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default Collaborazioni;
